import { updateBasketItem, selectBasketItem, extendFormToMap, selectBasketCheckPriceItem } from 'containers/OrderDetails/basketSlice'
import { getEndDate, getRangeFromDates, DAY, dateToYMDformat, YEAR, toLongDate } from 'utils/date'
import DateRangePickerPopup from 'components/pickers/DateRangePickerPopup'
import { DatePicker } from 'components/pickers/DateTimePicker'
import { LOCATION_STATES, STATUS } from 'utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import RentalPeriodPopup from './RentalPeriodPopup'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/icons/Checkbox'
import usePopupState from 'hooks/usePopupState'
import Range from 'components/pickers/Range'
import { displayPrice } from 'utils/string'
import Loading from 'components/Loading'
import { useLayoutEffect } from 'react'
import Error from 'components/Error'
import DateTile from './DateTile'
import './style.scss'

const Calendar = ({ isDesktop = false }) => {
  const { id } = useParams()
  const { reservation, start, end, rangeDate: range = { m: 0, d: 0 } } = useSelector(state => selectBasketItem(state, id))
  const { status = STATUS.pending, clientCanChooseEndingDate, calendar, priceList, termiateDays, ownerNote } = useSelector(state => selectBasketCheckPriceItem(state, id))
  const { popup } = usePopupState()

  const { push } = useHistory()
  const dispatch = useDispatch()
  const { t, lang } = useTranslation()

  useLayoutEffect(() => { dispatch(extendFormToMap(false)) }, [dispatch])

  const calendarChange = (date, selectedStart) => {
    if (selectedStart) {
      dispatch(updateBasketItem({ identifier: id, values: { start: date, end: getEndDate(range, date), reservation: undefined } }))

    } else if (new Date(date) - new Date(start) > DAY) {
      const rangeDate = getRangeFromDates(date, start)
      const priceIdx = priceList.findIndex(({ m, d }) => (m > rangeDate.m) || (rangeDate.m === 0 && d > rangeDate.d))
      const priceObj = priceIdx > 0 ? priceList[priceIdx - 1] : priceList[priceList.length - 1]
      const { gross_price: monthlyPrice, gross_price_d: dailyPrice } = priceObj
      dispatch(updateBasketItem({ identifier: id, values: { end: date, rangeDate, dailyPrice, monthlyPrice, periodsToPay: 0 } }))
    }
  }

  const rangeCom = clientCanChooseEndingDate
    ? <Range
      range={range}
      ranges={priceList}
      disabled={clientCanChooseEndingDate}
      updateRange={obj => {
        const { m, d, gross_price: monthlyPrice, gross_price_d: dailyPrice } = obj
        dispatch(updateBasketItem({ identifier: id, values: { end: getEndDate({ m, d }, start), rangeDate: { m, d }, dailyPrice, monthlyPrice, periodsToPay: 0 } }))
      }}
    />
    : null

  return (
    <div className='calendar'>
      {popup === 'info' && <RentalPeriodPopup />}
      {popup?.type === 'calendar' && <DateRangePickerPopup
        endDate={end ? new Date(end) : undefined}
        startDate={new Date(start || Date.now())}
        rangeCom={rangeCom}
        selectedStart={!!popup?.selected}
        disabledEndDate={!clientCanChooseEndingDate}
        minStartDate={calendar?.minDate ? new Date(calendar.date) : new Date()}
        maxStartDate={calendar?.maxRentalStartingDate ? new Date(calendar?.maxRentalStartingDate) : undefined}
        minEndDate={priceList.length > 0 ? getEndDate(priceList[0], start, true) : new Date(new Date(start || Date.now()).getTime() + DAY)}
        onChange={calendarChange} />}

      <div className='calendar__main'>
        {ownerNote && <div className='ownershipInfo'>
          <p dangerouslySetInnerHTML={{ __html: ownerNote }} />
        </div>}

        {status === STATUS.succeed && isDesktop && <div className='additional'>
          <div className='title'>
            <div className='group'>
              <span>{t('calendar.contractDuration')}</span>
              <i className='icon icon--quest' onClick={() => push(window.location.pathname, LOCATION_STATES.popup('info'))}></i>
            </div>
          </div>

          <div className='list'>
            {priceList && priceList?.at(-1)?.m === -1 && <div
              className={`list__element --big --autoHeight ${!end ? '--selected' : ''}`}
              onClick={() => {
                if (end) {
                  const { m, d, gross_price: monthlyPrice, gross_price_d: dailyPrice } = priceList.at(-1) || {}
                  dispatch(updateBasketItem({ identifier: id, values: { end: undefined, rangeDate: { m, d }, dailyPrice, monthlyPrice } }))
                }
              }}>
              <div className='element__group group'>
                <div className='element__title --secFont'>{t('calendar.indefinitePeriod')}</div>
                <Checkbox selected={!end} />
              </div>
              <div className='element__desc'>
                <div>{t('basket.periodOfNotice')}: {termiateDays} {t(`basket.${termiateDays === 1 ? 'day' : 'days'}`)}. {t('calendar.billingInformationDesc')}</div>
              </div>
            </div>}
            {clientCanChooseEndingDate && <div
              className={`list__element --big --autoHeight ${end ? '--selected' : ''}`}
              onClick={() => {
                if (!end) {
                  const { m, d, gross_price: monthlyPrice, gross_price_d: dailyPrice } = priceList.at(0) || {}
                  dispatch(updateBasketItem({ identifier: id, values: { end: getEndDate({ m, d }, start), rangeDate: { m, d }, dailyPrice, monthlyPrice } }))
                }
              }}>
              <div className='element__group group'>
                <div className='element__title --secFont'>{t('calendar.definitePeriod')}</div>
                <Checkbox selected={!!end} />
              </div>
              <div className='element__desc'>
                <div>{t('calendar.duringTheRentalPeriodItWillBePossibleToExtendThisPeriod')}</div>
              </div>
            </div>}
          </div>
        </div>}

        <div className='title'>
          <div className='group'>
            <span>{t(isDesktop ? 'calendar.dateSelection' : 'calendar.leasePeriod')}</span>
            {!isDesktop && <i className='icon icon--quest' onClick={() => push(window.location.pathname, LOCATION_STATES.popup('info'))} />}
          </div>
        </div>

        {status === STATUS.failed ? <Error />
          : status === STATUS.succeed ? <>
            <div className='center'>
              <div className='center__main'>
                {isDesktop
                  ? <div className='inputs'>
                    <DatePicker
                      name='startDate'
                      label={t('calendar.startDate')}
                      minDate={calendar?.minDate || dateToYMDformat()}
                      maxDate={calendar?.maxRentalStartingDate || dateToYMDformat(new Date(Date.now() + (5 * YEAR)))}
                      value={start}
                      setValue={dateString => {
                        const date = new Date(dateString)
                        if (calendar?.maxRentalStartingDate && date <= new Date(calendar?.maxRentalStartingDate)) {
                          calendarChange(dateString, true)
                        }
                      }}
                    />
                    <DatePicker
                      name='endDate'
                      label={t('calendar.endDate')}
                      placeholder={t('calendar.indefinitePeriod')}
                      disabled={!clientCanChooseEndingDate}
                      minDate={priceList.length > 0 ? getEndDate(priceList[0], start) : dateToYMDformat(new Date(start).getTime() + DAY)}
                      maxDate={dateToYMDformat(new Date(Date.now() + (5 * YEAR)))}
                      value={end}
                      setValue={dateString => {
                        calendarChange(dateString, false)
                      }}
                    />
                  </div>
                  : <div className='calendar__inputs center'>
                    <DateTile
                      start
                      date={start}
                      action={() => {
                        push(window.location.pathname, LOCATION_STATES.popup({ type: 'calendar', selected: true }))
                      }}
                    />
                    <DateTile
                      date={end}
                      disabled={!clientCanChooseEndingDate}
                      action={() => {
                        push(window.location.pathname, LOCATION_STATES.popup({ type: 'calendar', selected: false }))
                      }}
                    />
                  </div>}

                <div className='calendar__range'>
                  {rangeCom}
                </div>
              </div>
            </div>

            {reservation && (({ calendar, grossPrice }) => {
              const startDate = new Date(calendar?.start)
              const endDate = new Date(calendar?.end)
              return <div className='calendar__info'>
                <p>{t('basket.reservationForTheMissingPeriod')}</p>
                <div>
                  <span>{t('calendar.forThePeriod')}:&nbsp;</span>
                  <span>{toLongDate(startDate, lang, true)} - {toLongDate(endDate, lang, true)}</span>
                </div>
                <div>
                  <span>{t('calendar.fee')}:&nbsp;</span>
                  <span>{displayPrice(grossPrice)}</span>
                </div>
              </div>
            })(reservation)}

            {!isDesktop && <div className='calendar__info'>
              <p>{t('basket.periodOfNotice')}: {termiateDays} {t(`basket.${termiateDays === 1 ? 'day' : 'days'}`)}</p>
              <div>{t('calendar.billingInformationDesc')}</div>
            </div>}
          </>
            : <Loading middleOfPage />}
      </div>
    </div>
  )
}

export default Calendar